import { Box, Grid, Typography } from "@mui/material";

import interfaceImage from "../../../static/images/nam_universal_ii/nam-universal-ii-interface.webp";
import pedalsImage from "../../../static/images/nam_universal_ii/nam-universal-ii-pedals.webp";
import cabinetImage from "../../../static/images/nam_universal_ii/nam-universal-ii-cabinet.webp";
import intellEQImage from "../../../static/images/nam_universal_ii/nam-universal-ii-intelleq.webp";
import postFXImage from "../../../static/images/nam_universal_ii/nam-universal-ii-post-fx.webp";
import contentBrowserImage from "../../../static/images/nam_universal_ii/nam-universal-ii-content-browser.webp";
import midiEditorImage from "../../../static/images/nam_universal_ii/nam-universal-ii-midi-editor.webp";
import standaloneSettingsImage from "../../../static/images/nam_universal_ii/nam-universal-ii-standalone-settings.webp";
import asioLogo from "../../../static/images/asio_logo.jpg";

const DEEP_BACKGROUND = "#080b0d";
const CHARCOAL_BACKGROUND = "#111517";
const SLATE_BACKGROUND = "#20262a";
const NAVY_BACKGROUND = "#182b3d";
const BLUE_BACKGROUND = "#294f70";
const TEAL_BACKGROUND = "#15545a";
const TEAL_ACCENT = "#237c83";
const MIDI_BLUE = "#173147";
const STANDALONE_BACKGROUND = "#eef1f2";

const edgeGradient = (top, center, bottom) =>
  `linear-gradient(180deg, ${top} 0%, ${center} 10%, ${center} 90%, ${bottom} 100%)`;

const OVERVIEW_BACKGROUND = edgeGradient(DEEP_BACKGROUND, CHARCOAL_BACKGROUND, SLATE_BACKGROUND);
const CAPTURE_BACKGROUND = edgeGradient(SLATE_BACKGROUND, CHARCOAL_BACKGROUND, NAVY_BACKGROUND);
const CABINET_BACKGROUND = edgeGradient(NAVY_BACKGROUND, BLUE_BACKGROUND, TEAL_BACKGROUND);
const INTELLEQ_BACKGROUND = edgeGradient(TEAL_BACKGROUND, TEAL_ACCENT, CHARCOAL_BACKGROUND);
const POST_FX_BACKGROUND = edgeGradient(CHARCOAL_BACKGROUND, CHARCOAL_BACKGROUND, SLATE_BACKGROUND);
const UTILITIES_BACKGROUND = edgeGradient(SLATE_BACKGROUND, DEEP_BACKGROUND, NAVY_BACKGROUND);
const CONTENT_BACKGROUND = edgeGradient(NAVY_BACKGROUND, CHARCOAL_BACKGROUND, DEEP_BACKGROUND);
const MIDI_BACKGROUND = edgeGradient(DEEP_BACKGROUND, MIDI_BLUE, STANDALONE_BACKGROUND);

const ProductShot = ({ src, alt, maxWidth = "1440px", standalone = false }) => (
  <Box
    className="nam-universal-ii__product-shot"
    sx={{ maxWidth, mx: "auto" }}
  >
    <img
      loading="lazy"
      src={src}
      alt={alt}
      width="100%"
      style={{
        display: "block",
        marginTop: standalone ? "-5.64%" : "-1.875%",
      }}
    />
  </Box>
);

const Section = ({ children, background = CHARCOAL_BACKGROUND, color = "#fff" }) => (
  <Box
    sx={{
      px: { xs: 2, sm: 4, md: 7, lg: 10 },
      py: { xs: 7, md: 10 },
      mt: "-1px",
      background,
      color,
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    {children}
  </Box>
);

const Details = ({ children }) => (
  <Grid container justifyContent="center">
    <Grid item xs={12} md={7} lg={6} sx={{ mt: 4 }}>
      {children}
    </Grid>
  </Grid>
);

const OverviewBanner = () => (
  <Grid
    container
    sx={{
      width: "100%",
      pt: "80px",
      pb: { xs: 8, md: 20 },
      backgroundSize: "cover",
      backgroundImage: OVERVIEW_BACKGROUND,
    }}
    justifyContent="center"
  >
    <Grid
      item
      id="overview"
      xs={12}
      md={6}
      justifyContent="center"
      alignItems="center"
      sx={{ px: { xs: 3, sm: 5, md: 10 } }}
    >
      <Typography variant="h3" sx={{ mb: { xs: 2, md: 5 } }}>
        About the plugin
      </Typography>
      <Typography sx={{ fontSize: { md: "23px", xs: "16px" } }} variant="body2">
        <b>NAM Universal II</b> is a complete guitar and bass signal chain built around
        Neural Amp Modeler capture playback. Capture-based pedals and amplifiers flow
        into cabinet processing, IntellEQ tone shaping, chorus, delay, reverb, and
        global output tools in one interface.
      </Typography>
      <Typography sx={{ mt: { xs: 4, md: 7 }, fontSize: { md: "23px", xs: "16px" } }} variant="body2">
        Work in mono or stereo for practice, recording, live rigs, reamping, and capture
        auditioning. The signal path stays visible, so every stage can be shaped without
        leaving the plug-in.
      </Typography>
    </Grid>
    <Grid item xs={12} md={6} sx={{ mt: { xs: 6, md: 0 }, pr: { xs: 3, sm: 5, md: 10 }, pl: { xs: 3, md: 0 } }}>
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", width: "100%" }}>
        <ProductShot src={interfaceImage} alt="NAM Universal II main interface" />
      </Box>
    </Grid>
  </Grid>
);

const CaptureBanner = () => (
  <Section background={CAPTURE_BACKGROUND}>
    <Box sx={{ width: "100%" }}>
      <Typography variant="h3" sx={{ textAlign: "center", mb: 4 }}>
        NAM Pedals and Amplifiers
      </Typography>
    </Box>
    <Grid container justifyContent="center">
      <Grid item xs={12} md={10}>
        <ProductShot src={pedalsImage} alt="NAM Universal II pedal and amplifier capture stages" />
      </Grid>
    </Grid>
    <Details>
      <Typography variant="h6" sx={{ textAlign: "left" }}>
        <b>Separate capture slots:</b> Keep NAM pedal captures before the dedicated NAM
        amplifier stage, with input, output, and tone controls for each part of the rig.
      </Typography>
      <br />
      <Typography variant="h6" sx={{ textAlign: "left" }}>
        <b>Compatible content:</b> Both A1 and A2 NAM captures are supported. Load .nam
        files from the Content Browser or drag them directly onto the Pedals and Amplifier pages.
      </Typography>
      <br />
      <Typography variant="h6" sx={{ textAlign: "left" }}>
        <b>Fast auditioning:</b> Step through compatible captures while the complete signal
        chain remains ready around them.
      </Typography>
    </Details>
  </Section>
);

const CabinetBanner = () => (
  <Section background={CABINET_BACKGROUND}>
    <Grid container justifyContent="center">
      <Grid item xs={12} md={10}>
        <ProductShot src={cabinetImage} alt="NAM Universal II four-slot cabinet system" />
      </Grid>
    </Grid>
    <Details>
      <Typography sx={{ textAlign: "center", mb: 3 }} variant="h3">
        Cabinets and IRs
      </Typography>
      <Typography sx={{ textAlign: "center", mb: 3 }} variant="h6">
        Blend up to four cabinet bundles or conventional impulse responses. Where the
        content supports it, choose microphone, position, and distance, then set pan,
        blend, polarity, mute, and solo for each slot.
      </Typography>
      <Typography sx={{ textAlign: "center" }} variant="h6">
        Automatic alignment compensation, Oomph, Sparkle, low and high cut, Drive,
        IR Shift, and IR export complete the cabinet stage.
      </Typography>
    </Details>
  </Section>
);

const IntellEQBanner = () => (
  <Section background={INTELLEQ_BACKGROUND}>
    <Grid container justifyContent="center">
      <Grid item xs={12} md={10}>
        <ProductShot src={intellEQImage} alt="NAM Universal II IntellEQ tone shaping" />
      </Grid>
    </Grid>
    <Details>
      <Typography sx={{ textAlign: "center", mb: 3 }} variant="h3">
        IntellEQ Tone Shaping
      </Typography>
      <Typography sx={{ textAlign: "center" }} variant="h6">
        Shape the areas that matter with Rumble, Definition, Bite, Air, Scoop I,
        Scoop II, and Smoothness: focused musical tone controls that keep the workflow
        direct after the amplifier stage.
      </Typography>
    </Details>
  </Section>
);

const PostFXBanner = () => (
  <Section background={POST_FX_BACKGROUND}>
    <Grid container justifyContent="center">
      <Grid item xs={12} md={10}>
        <ProductShot src={postFXImage} alt="NAM Universal II post effects" />
      </Grid>
    </Grid>
    <Box sx={{ width: "100%" }}>
      <Typography variant="h3" sx={{ textAlign: "center", mt: 4, mb: 3 }}>
        Post Effects
      </Typography>
    </Box>
    <Details>
      <Typography variant="h6" sx={{ textAlign: "left" }}>
        <b>Chorus:</b> Add movement and width with speed, depth, tone, and wet/dry mix.
      </Typography>
      <br />
      <Typography variant="h6" sx={{ textAlign: "left" }}>
        <b>Delay:</b> Use stereo or tempo-aware repeats with sync, tap, link, ping-pong,
        modulation, filtering, feedback, and mix controls.
      </Typography>
      <br />
      <Typography variant="h6" sx={{ textAlign: "left" }}>
        <b>Reverb:</b> Move from focused spaces to ambient textures with character,
        damping, shimmer, mix, and size.
      </Typography>
    </Details>
  </Section>
);

const UtilitiesBanner = () => (
  <Section background={UTILITIES_BACKGROUND}>
    <Grid container justifyContent="center" alignItems="center">
      <Grid item xs={12} md={6} sx={{ p: { xs: 0, lg: 6 } }}>
        <ProductShot src={interfaceImage} alt="NAM Universal II performance and utility controls" />
      </Grid>
      <Grid item xs={12} md={6} lg={4} sx={{ mt: { xs: 5, md: 0 } }}>
        <Typography variant="h3">Utilities</Typography>
        <br />
        <Typography variant="h6" sx={{ textAlign: "left" }}><b>Gate and Doubler:</b> Control noise and create stereo width from a mono performance.</Typography>
        <br />
        <Typography variant="h6" sx={{ textAlign: "left" }}><b>Drop and Room:</b> Transpose from 0 to 12 semitones and add room ambience near the end of the chain.</Typography>
        <br />
        <Typography variant="h6" sx={{ textAlign: "left" }}><b>Tight and Smooth:</b> Apply focused low- and high-range control before the final output.</Typography>
        <br />
        <Typography variant="h6" sx={{ textAlign: "left" }}><b>Performance:</b> Set input calibration, choose 1x, 2x, 4x, or 8x oversampling, and tune with the built-in tuner.</Typography>
      </Grid>
    </Grid>
  </Section>
);

const ContentBanner = () => (
  <Section background={CONTENT_BACKGROUND}>
    <Grid container justifyContent="center">
      <Grid item xs={12} md={10}>
        <ProductShot src={contentBrowserImage} alt="NAM Universal II Content Browser" />
      </Grid>
    </Grid>
    <Details>
      <Typography sx={{ textAlign: "center", mb: 3 }} variant="h3">
        Content Browser and Presets
      </Typography>
      <Typography sx={{ textAlign: "center" }} variant="h6">
        <b>100+ hand-picked capture packs are included.</b> Easily import your own
        library, keep it organized with simple tag management, and find the right sound
        quickly with search and filtering. Browse local NAM profiles, impulse responses,
        cabinet bundles, and available expansions in one place, mark favourites, revisit
        recent content, preview where available, and save the complete rig as a preset.
      </Typography>
    </Details>
  </Section>
);

const MIDIBanner = () => (
  <Section background={MIDI_BACKGROUND}>
    <Grid container justifyContent="center" alignItems="center">
      <Grid item xs={12} md={6} sx={{ p: { xs: 0, lg: 6 } }}>
        <ProductShot src={midiEditorImage} alt="NAM Universal II MIDI Mapping Editor" />
      </Grid>
      <Grid item xs={12} md={6} lg={4} sx={{ mt: { xs: 5, md: 0 } }}>
        <Typography variant="h3">MIDI Control</Typography>
        <br />
        <Typography variant="h6" sx={{ textAlign: "left" }}>
          Right-click supported controls for quick MIDI Learn, or use the MIDI Mapping
          Editor for detailed parameter, switch, and preset assignments.
        </Typography>
        <br />
        <Typography variant="h6" sx={{ textAlign: "left" }}>
          Save reusable MIDI maps and recall presets from a controller for consistent
          practice, recording, and live-rig setups.
        </Typography>
      </Grid>
    </Grid>
  </Section>
);

const StandaloneBanner = () => (
  <Grid
    container
    sx={{ width: "100%", pt: 0, pb: 0, background: STANDALONE_BACKGROUND }}
    justifyContent="center"
  >
    <Grid item xs={12} md={12} justifyContent="left" alignItems="center">
      <Grid container sx={{ width: "100%" }} alignItems="center" justifyContent="center">
        <Grid
          item
          xs={12}
          md={6}
          alignItems="center"
          sx={{ p: { xs: 4, md: 10 }, mb: "0 !important", textAlign: "center" }}
        >
          <Typography variant="h3" sx={{ fontWeight: "bold", color: "#000" }}>
            No Dependencies
          </Typography>
          <Typography
            fontFamily="montserrat"
            sx={{
              textAlign: { xs: "left", md: "center" },
              mt: 2,
              fontSize: { md: "24px", xs: "16px" },
              color: "#000",
            }}
          >
            Don&apos;t have access to either a VST3, AAX or AU host? No worries, you can
            still run NAM Universal II all on its own!
          </Typography>
          <Typography
            fontFamily="montserrat"
            sx={{
              textAlign: { xs: "left", md: "center" },
              mt: 2,
              fontSize: { md: "24px", xs: "16px" },
              color: "#000",
            }}
          >
            A fully standalone version is included with your download.
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          md={12}
          alignItems="center"
          sx={{ px: { xs: 3, md: 0 }, color: "#000", display: "flex" }}
          justifyContent="center"
        >
          <Box sx={{ width: "100%", maxWidth: "700px" }}>
            <ProductShot
              standalone
              src={standaloneSettingsImage}
              alt="NAM Universal II standalone audio and MIDI settings"
            />
          </Box>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          alignItems="center"
          sx={{ p: { xs: 4, md: 10 }, mt: "0 !important", textAlign: "left", color: "#000" }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "#000" }}>
            System Requirements
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1, mt: 2, mb: 2 }}>
            <Box><b>Formats:</b> NAM Universal II is available in 64-bit VST3, AU, AAX and Standalone format.</Box>
            <Box><b>OS:</b> WindowsÂ® 10 64 Bit (latest update) or MacOSÂ® 10.13+</Box>
            <Box><b>Processor:</b> Minimum AVX support (Intel HaswellÂ® or AMD ExcavatorÂ® and up)</Box>
            <Box><b>Memory:</b> 8 GB</Box>
            <Box><b>Storage:</b> 4GB+ of free space</Box>
            <Box><b>Account:</b> A Free WaveMind account is required to register your license.</Box>
            <Box><b>Internet:</b> An internet connection is required to activate your license through your WaveMind account.</Box>
          </Box>
          <Typography fontSize={12}>
            <b>Note: </b>these specs are estimates and are considered the bare minimum.
            For a better experience, higher specs are recommended.
          </Typography>
          <img
            loading="lazy"
            alt="ASIO"
            width="100%"
            src={asioLogo}
            style={{ maxWidth: "50px", marginTop: "10px", borderRadius: "0" }}
          />
        </Grid>
      </Grid>
    </Grid>
  </Grid>
);

const ProductSections = () => (
  <>
    <OverviewBanner />
    <CaptureBanner />
    <CabinetBanner />
    <IntellEQBanner />
    <PostFXBanner />
    <UtilitiesBanner />
    <ContentBanner />
    <MIDIBanner />
    <StandaloneBanner />
  </>
);

export default ProductSections;
